/**
 * Design tokens for the Team Leave Tracker plugin.
 *
 * Colours are derived from the active Backstage theme via {@link themeTokens}
 * so the plugin follows the app's light/dark mode, while the typography keeps
 * the plugin's distinct archival fonts.
 */

import type { Theme } from '@mui/material/styles';
import { useEffect } from 'react';

/**
 * Fixed brand accent, used as a fallback and as the data colour for "sick"
 * leave. UI accents are driven by the theme's primary colour (see
 * {@link themeTokens}) so they adapt to the surrounding app theme.
 */
export const terracotta = '#C84B31';

/** Semantic colour tokens used across the plugin's components. */
export interface ThemeTokens {
  /** Page background. */
  paper: string;
  /** Slightly raised surface for cards, panels and inputs. */
  surface: string;
  /** Primary text / headers. */
  navy: string;
  /** Accent for actions, "today" and active highlights. */
  terracotta: string;
  /** Readable text/icon colour placed on top of the accent colour. */
  onAccent: string;
  /** Muted secondary text. */
  muted: string;
  /** Hairline border colour. */
  border: string;
  /** Stronger hairline used on hover / active states. */
  borderStrong: string;
  /** Subtle background used for hover states. */
  hover: string;
}

/**
 * Maps the active MUI/Backstage theme to the plugin's semantic tokens. The
 * token names are kept stable so component styles read the same regardless of
 * light or dark mode.
 */
export function themeTokens(theme: Theme): ThemeTokens {
  return {
    paper: theme.palette.background.default,
    surface: theme.palette.background.paper,
    navy: theme.palette.text.primary,
    terracotta: theme.palette.primary.main,
    onAccent: theme.palette.primary.contrastText,
    muted: theme.palette.text.secondary,
    border: theme.palette.divider,
    borderStrong: theme.palette.action.disabled,
    hover: theme.palette.action.hover,
  };
}

export const fonts = {
  body: `'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
  mono: `'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace`,
} as const;

const FONT_LINK_ID = 'team-leave-tracker-fonts';
const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap';

/**
 * Injects the DM Sans / JetBrains Mono web fonts once. Falls back silently to
 * the system stacks defined above when the fonts cannot be loaded.
 */
export function useArchivalFonts(): void {
  useEffect(() => {
    if (document.getElementById(FONT_LINK_ID)) {
      return;
    }
    const link = document.createElement('link');
    link.id = FONT_LINK_ID;
    link.rel = 'stylesheet';
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }, []);
}
